import React, { useState, useMemo } from 'react';
import { Inbox, Edit2, Trash2, ArrowUp, Filter, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { OrkaPane, OrkaTaskRow } from '../orka';

/**
 * BacklogPane — V3 migrated (Phase 2 cascade).
 *
 * Switched outer shell to <OrkaPane> and inner cards to <OrkaTaskRow>. All
 * legacy test-ids (`backlog-task-${id}`, `task-actions-${id}`) preserved.
 *
 * Rule 1 (no raw bg-slate-* outside design-system) — filter chips intentionally
 * retain raw colour utilities for now, gated by §10 adapter strategy.
 * TODO @design-system 2026-06-30: refactor filter chips to OrkaButton/OrkaBadge.
 */

const BacklogPane = ({
  tasks,
  totalEffort,
  taskCount,
  onTaskUpdate,
  onTaskEdit,
  onTaskDelete,
  onMoveToToday,
  onMoveToBacklog,
  draggingTask,
  onDragStart,
  onDragEnd,
}) => {
  const [isOver, setIsOver] = useState(false);
  const { workAreaName } = useWorkAreas();
  const [showFilterSort, setShowFilterSort] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterPriorities, setFilterPriorities] = useState(new Set());
  const [filterWorkAreas, setFilterWorkAreas] = useState(new Set());
  const [filterStatuses, setFilterStatuses] = useState(new Set());
  const [sortBy, setSortBy] = useState('task_created_at');
  const [sortDir, setSortDir] = useState('desc');

  const toggleFilter = (set, setter, value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsOver(true); };
  const handleDragLeave = () => setIsOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (draggingTask && onMoveToBacklog) onMoveToBacklog(draggingTask);
    onDragEnd();
  };

  const workAreas = useMemo(() => {
    const areas = new Set(tasks.map(t => t.work_area_id || 'General'));
    return Array.from(areas).sort();
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (filterText) {
      const q = filterText.toLowerCase();
      result = result.filter(t => t.task_title.toLowerCase().includes(q));
    }
    if (filterPriorities.size > 0) result = result.filter(t => filterPriorities.has(String(t.task_priority ?? 3)));
    if (filterWorkAreas.size > 0) result = result.filter(t => filterWorkAreas.has(t.work_area_id || ''));
    if (filterStatuses.size > 0) result = result.filter(t => filterStatuses.has(t.task_status || 'To Do'));
    result = [...result].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'task_priority') { aVal = a.task_priority ?? 3; bVal = b.task_priority ?? 3; }
      else if (sortBy === 'task_estimated_effort') { aVal = a.task_estimated_effort || 1; bVal = b.task_estimated_effort || 1; }
      else if (sortBy === 'task_title') { aVal = (a.task_title || '').toLowerCase(); bVal = (b.task_title || '').toLowerCase(); }
      else { aVal = a.task_created_at || ''; bVal = b.task_created_at || ''; }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [tasks, filterText, filterPriorities, filterWorkAreas, filterStatuses, sortBy, sortDir]);

  const hasFilters = filterText || filterPriorities.size > 0 || filterWorkAreas.size > 0 || filterStatuses.size > 0;

  const FilterStrip = (
    <div className="space-y-2">
      <Input
        type="text"
        placeholder="Search tasks..."
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        className="h-7 text-xs"
      />
      <div>
        <div className="text-[10px] font-medium orka-text-tertiary mb-1">Priority</div>
        <div className="flex flex-wrap gap-1">
          {['-1', '0', '1', '2', '3', '4', '5'].map(p => (
            <button
              key={p}
              onClick={() => toggleFilter(filterPriorities, setFilterPriorities, p)}
              className={`text-[10px] px-1.5 py-0.5 rounded border orka-transition-colors ${
                filterPriorities.has(p)
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'orka-surface-base orka-text-secondary orka-border-default hover:orka-border-strong'
              }`}
            >P{p}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-medium orka-text-tertiary mb-1">Work Area</div>
        <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
          {workAreas.map(area => (
            <button
              key={area}
              onClick={() => toggleFilter(filterWorkAreas, setFilterWorkAreas, area)}
              className={`text-[10px] px-1.5 py-0.5 rounded border orka-transition-colors truncate max-w-[80px] ${
                filterWorkAreas.has(area)
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'orka-surface-base orka-text-secondary orka-border-default hover:orka-border-strong'
              }`}
              title={area}
            >{workAreaName(area) || area || 'None'}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-medium orka-text-tertiary mb-1">Status</div>
        <div className="flex flex-wrap gap-1">
          {['To Do', 'In Progress', 'Blocked', 'Partial'].map(s => (
            <button
              key={s}
              onClick={() => toggleFilter(filterStatuses, setFilterStatuses, s)}
              className={`text-[10px] px-1.5 py-0.5 rounded border orka-transition-colors ${
                filterStatuses.has(s)
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'orka-surface-base orka-text-secondary orka-border-default hover:orka-border-strong'
              }`}
            >{s}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-1 pt-1 border-t orka-border-subtle">
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-7 text-xs flex-1">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="task_created_at">Created</SelectItem>
            <SelectItem value="task_priority">Priority</SelectItem>
            <SelectItem value="task_estimated_effort">Effort</SelectItem>
            <SelectItem value="task_title">Title</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
          <ArrowUpDown className="w-3 h-3" />
        </Button>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-7 text-[10px] orka-text-danger" onClick={() => {
            setFilterPriorities(new Set()); setFilterWorkAreas(new Set()); setFilterStatuses(new Set()); setFilterText('');
          }}>Clear</Button>
        )}
      </div>
    </div>
  );

  const FilterToggleSlot = (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 px-2 text-xs"
      onClick={() => setShowFilterSort(!showFilterSort)}
      data-testid="backlog-pane-filter-toggle"
    >
      <Filter className="w-3 h-3 mr-1" />
      {showFilterSort ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
    </Button>
  );

  return (
    <OrkaPane
      title="Backlog"
      intent="capture"
      icon={Inbox}
      stats={{ effort: totalEffort, filtered: filteredTasks.length, total: taskCount }}
      filterSlot={showFilterSort ? FilterStrip : null}
      filterActive={!!hasFilters}
      rightSlot={FilterToggleSlot}
      isDropTarget={isOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      testId="backlog-pane"
    >
      {filteredTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full orka-text-tertiary">
          <Inbox className="w-8 h-8 mb-2" aria-hidden="true" />
          <p className="text-sm">{hasFilters ? 'No matching tasks' : 'Backlog is empty'}</p>
          <p className="text-xs mt-1">{hasFilters ? 'Try different filters' : 'Tasks without a date appear here'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map((task) => (
            <BacklogTaskRow
              key={task.task_id}
              task={task}
              isDragging={draggingTask?.task_id === task.task_id}
              onEdit={onTaskEdit}
              onDelete={onTaskDelete}
              onMoveToToday={onMoveToToday}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              workAreaName={workAreaName}
            />
          ))}
        </div>
      )}
    </OrkaPane>
  );
};

// V3 row: wraps OrkaTaskRow with the popover menu + dnd glue
const BacklogTaskRow = ({ task, isDragging, onEdit, onDelete, onMoveToToday, onDragStart, onDragEnd, workAreaName }) => {
  const enriched = {
    ...task,
    work_area_name: task.work_area_id && task.work_area_id !== 'General' ? workAreaName(task.work_area_id) : null,
  };
  return (
    <Popover>
      <OrkaTaskRow
        task={enriched}
        movementState={isDragging ? 'dragging' : 'idle'}
        onClick={() => onEdit(task)}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          onDragStart(task);
        }}
        onDragEnd={onDragEnd}
        rightSlot={
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Task actions"
              onClick={(e) => e.stopPropagation()}
              data-testid={`task-actions-${task.task_id}`}
              className="inline-flex items-center justify-center w-5 h-5 rounded-md orka-text-tertiary hover:orka-text-primary hover:orka-surface-subtle orka-transition-colors orka-focus-ring shrink-0"
            >
              <span className="text-base leading-none" aria-hidden="true">⋯</span>
            </button>
          </PopoverTrigger>
        }
        testId={`backlog-task-${task.task_id}`}
      />
      <PopoverContent className="w-40 p-1" align="end">
        <Button variant="ghost" className="w-full justify-start text-sm h-8" onClick={() => onEdit(task)}>
          <Edit2 className="h-3 w-3 mr-2" /> Edit
        </Button>
        <Button variant="ghost" className="w-full justify-start text-sm h-8" onClick={() => onMoveToToday(task.task_id)}>
          <ArrowUp className="h-3 w-3 mr-2" /> Move to Today
        </Button>
        <Button variant="ghost" className="w-full justify-start text-sm orka-text-danger h-8 hover:orka-bg-danger-soft dark:hover:bg-red-950/30" onClick={() => onDelete(task.task_id)}>
          <Trash2 className="h-3 w-3 mr-2" /> Delete
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default BacklogPane;
